/**
 * Canonical consent helper for non-essential (analytics / advertising) trackers.
 *
 * Quebec Law 25 (Bill 64) and PIPEDA treat analytics and advertising cookies as
 * non-essential: they require opt-in BEFORE they are set. This module is the one
 * place that answers "may we run a non-essential tracker yet?" so every tracker
 * (PostHog, the Meta Pixel, Sentry session replay) gates on the same source of
 * truth instead of each rolling its own localStorage read.
 *
 * Strictly necessary cookies (auth / session) are exempt and are never gated
 * here — they always run.
 *
 * The stored value is written by src/components/CookieConsent.tsx and read back
 * by the tracker initializers. Default state (no value, or "rejected") = OFF,
 * which is the privacy-first default required for CA / EU visitors.
 */

/**
 * localStorage key holding the visitor's consent choice.
 *
 * Re-exported from src/lib/posthog.ts historically; defined here now so this
 * module owns the contract. posthog.ts re-exports it for backwards-compat.
 */
export const CONSENT_STORAGE_KEY = "cr:cookie-consent:v1";

export type ConsentChoice = "accepted" | "rejected";

/** Event dispatched by the banner the moment the visitor makes a choice. */
export const CONSENT_EVENT = "cr:consent";

/** Read the stored choice, or null when the visitor has not decided yet. */
export function readConsentChoice(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
}

/**
 * True only when the visitor has explicitly accepted analytics cookies.
 *
 * This is the single gate every non-essential tracker must check before it
 * initializes. An absent or "rejected" value returns false (privacy-first).
 */
export function hasAnalyticsConsent(): boolean {
  return readConsentChoice() === "accepted";
}
